import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { todayQuery, useMe, useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { Reason } from "../design/components/Reason";
import "../design/components/button.css";
import { SequencesTodayBlock } from "../home/SequencesTodayBlock";
import { TodayBlock } from "../home/TodayBlock";
import "../home/home.css";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { firstNameInTab, reportsScopeOf, useAccess, useMenuSubject } from "../session/access";
import { settingsSectionsOf } from "./settings/sections";
import "./auth.css";

// La Home: chi è la persona (salutata per nome, senza il cognome) e in quale azienda lavora la scheda, poi i numeri di oggi (piano M5,
// Task 22; ruling H8). Le guardie del router hanno già controllato sessione e azienda. «Esci» sta in «Il tuo account», in fondo alla
// rail. «Vai alle Impostazioni» c'è quando le Impostazioni sono nel menu della scheda: nella Vista Commerciale, soltanto se il
// Commerciale scelto ne ha almeno una sezione (decisione del 2 ottobre 2026).
//
// Sotto il saluto, per chi vede almeno i suoi report (nella «Guarda come…» la persona osservata, come decide il server), `GET
// /api/today`: «Oggi» quando il server lo dice (`showToday`), poi «Sequenze rispettate oggi». Chi non vede nessun report non chiede
// nulla e legge ancora «Qui arriveranno…» (ruling H8a).
export function HomePage() {
  const t = useT();
  const me = useMe();
  const access = useAccess();
  const subject = useMenuSubject();
  const settings = settingsSectionsOf(subject).length > 0;
  const asksToday = subject.access !== null && reportsScopeOf(subject.access) !== "none";
  const { context, isPlatform } = access;
  const organization = context.organization.name;
  const where = isPlatform
    ? t("home.inCompanyAsPlatform", { organization })
    : t("home.inCompany", { organization, role: context.role?.name ?? "" });

  return (
    <div className="g-home">
      <Card className="g-stack">
        <div>
          <h1>{t("common.greeting", { name: firstNameInTab(me, access) })}</h1>
          <p className="g-auth__muted">{where}</p>
        </div>
        {asksToday ? null : <p>{t("home.comingNext")}</p>}
        {settings ? (
          <div className="g-auth__actions">
            <Link to="/impostazioni" className="g-button" data-variant="secondary">
              {t("home.goToSettings")}
            </Link>
          </div>
        ) : null}
      </Card>
      {asksToday ? <TodayNumbers /> : null}
    </div>
  );
}

/** I numeri di oggi, con gli stati di attesa e d'errore (`UX_SYSTEM.md`). */
function TodayNumbers() {
  const t = useT();
  const today = useQuery(todayQuery(useTab()));
  const timeZone = useAccess().context.settings.timeZone;
  if (today.data) {
    return (
      <>
        {today.data.showToday ? <TodayBlock today={today.data} timeZone={timeZone} /> : null}
        <SequencesTodayBlock sequences={today.data.sequences} />
      </>
    );
  }
  if (today.isError) {
    return (
      <div className="g-home-problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, today.error)}
        </Reason>
        <Button onClick={() => void today.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  }
  return <p className="g-auth__muted">{t("common.loading")}</p>;
}
